import { Button, styled, Typography } from '@mui/material';
import { KycProcessStatus, KycRiskRating } from 'further-types/kyc';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';

import { kycProcessStatusLabels, kycRiskRatingLabels } from 'further-ui/labels';
import { pluralizedCount } from 'further-ui/utils';

const kycRiskRatingLabelsMap = Object.fromEntries(
  kycRiskRatingLabels.map((label) => [label.value, label.label]),
);

const KycButton = styled(Button)(({ theme }) => ({
  padding: 0,
  color: theme.palette.grey[600],
  textDecoration: 'underline',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
  minWidth: 'auto',
}));

type Props = {
  kycDetails: {
    eKyc: {
      idvProcessStatus?: KycProcessStatus;
      amlProcessStatus?: KycProcessStatus;
      riskRating?: KycRiskRating;
      manualApprovalNotes?: string;
      manualApprovalFiles?: Array<{
        path: string;
        name: string;
        previewUrl: string;
      }>;
      amlPotentialMatches: Array<{
        archived?: boolean;
      }>;
    };
    answers?: Array<{
      answerText?: string;
      answerFiles: Array<{
        path: string;
        name: string;
        previewUrl: string;
      }>;
    }>;
  };
};

const PreviousKycDetails: React.FC<Props> = ({ kycDetails }) => {
  const files = kycDetails.answers?.flatMap((answer) => answer.answerFiles);
  const manualApprovalFiles = kycDetails.eKyc.manualApprovalFiles;

  const isInformationCompleted = !!kycDetails.answers?.some(
    (answer) => !!answer.answerText || answer.answerFiles.length,
  );

  const potentialUnarchivedMatches = kycDetails.eKyc.amlPotentialMatches.filter(
    (match) => !match.archived,
  ).length;
  const potentialArchivedMatches = kycDetails.eKyc.amlPotentialMatches.filter(
    (match) => match.archived,
  ).length;

  const amlResult =
    potentialUnarchivedMatches === 0
      ? `No potential matches${
          potentialArchivedMatches > 0
            ? `. ${pluralizedCount(
                'hit',
                potentialArchivedMatches,
                'hits',
              )} archived.`
            : ''
        }`
      : pluralizedCount(
          'potential match',
          potentialUnarchivedMatches,
          'potential matches',
        );

  return (
    <Column fullWidth spacing="md">
      <FormField label="IDV status" spacing="xxs">
        <Row spacing="xs">
          <Typography variant="body2" color="grey.600">
            {kycDetails.eKyc.idvProcessStatus
              ? kycProcessStatusLabels[kycDetails.eKyc.idvProcessStatus]
              : '--'}
          </Typography>
        </Row>
      </FormField>
      <FormField label="AML status" spacing="xxs">
        <Row spacing="xs">
          <Typography variant="body2" color="grey.600">
            {kycDetails.eKyc.amlProcessStatus
              ? kycProcessStatusLabels[kycDetails.eKyc.amlProcessStatus]
              : '--'}
          </Typography>
        </Row>
      </FormField>
      <FormField label="AML result" spacing="xxs">
        <Typography variant="body2" color="grey.600">
          {amlResult}
        </Typography>
      </FormField>
      <FormField label="Risk rating" spacing="xs">
        <Row spacing="xxs" centered>
          <Typography variant="body2" color="grey.600">
            {kycDetails.eKyc.riskRating
              ? kycRiskRatingLabelsMap[kycDetails.eKyc.riskRating]
              : kycRiskRatingLabelsMap[KycRiskRating.Low]}
          </Typography>
        </Row>
      </FormField>
      <FormField label="Custom questions responses" spacing="xxs">
        <Typography variant="body2" color="grey.600">
          {isInformationCompleted ? 'Completed' : '--'}
        </Typography>
      </FormField>
      <FormField label="Custom questions files" spacing="xxs">
        {files?.length ? (
          files.map((file) => (
            <Row spacing="xs" key={file.path}>
              <Typography variant="body2" color="grey.600">
                {file.name}
              </Typography>
              <KycButton variant="text" color="primary" href={file.previewUrl}>
                Download
              </KycButton>
            </Row>
          ))
        ) : (
          <Typography variant="body2" color="grey.600">
            --
          </Typography>
        )}
      </FormField>
      {manualApprovalFiles?.length ? (
        <FormField label="Admin-uploaded files" spacing="xxs">
          {manualApprovalFiles.map((file) => (
            <Row spacing="xs" key={file.path}>
              <Typography variant="body2" color="grey.600">
                {file.name}
              </Typography>
              <KycButton variant="text" color="primary" href={file.previewUrl}>
                Download
              </KycButton>
            </Row>
          ))}
        </FormField>
      ) : null}
      <FormField label="Notes" spacing="xxs">
        <Typography variant="body2" color="grey.600">
          {kycDetails.eKyc.manualApprovalNotes ?? '--'}
        </Typography>
      </FormField>
    </Column>
  );
};

export default PreviousKycDetails;
